import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UserCog, Shield, Edit2, Plus, Search, Power, Key, Copy, X, Users,
  CheckCircle2, XCircle, UserPlus, Filter, ChevronDown, ChevronUp, Eye, EyeOff, Award, Trash2, AlertTriangle, Settings,
  Briefcase, Crown, RefreshCw, UserCheck, User, History, Mail, Clock, Send, AtSign, Bot
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import ModalPortal from '../../components/ModalPortal';
import MemberEmailHistoryModal from '../../components/admin/MemberEmailHistoryModal';
import ChangeEmailModal from '../../components/admin/ChangeEmailModal';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * USER TYPE SYSTEM — category layer that is independent of role.
 *   - Admin sets this manually per user.
 *   - It drives the top-level filter tabs (All / Members / Staff / Leadership / Admins).
 *   - It is NOT a permission lock. Permissions are controlled via the Manage Access modal.
 */
const USER_TYPES = [
  { value: 'member',     label: 'Member',     color: 'bg-slate-100 text-slate-700',       icon: User,       desc: 'Member portal access' },
  { value: 'staff',      label: 'Staff',      color: 'bg-blue-100 text-blue-700',         icon: Briefcase,  desc: 'Internal team member' },
  { value: 'leadership', label: 'Leadership', color: 'bg-purple-100 text-purple-700',     icon: Crown,      desc: 'Board / Executives' },
  { value: 'admin',      label: 'Admin',      color: 'bg-rose-100 text-rose-700',         icon: Shield,     desc: 'Super admin / system' },
];
const getUserTypeInfo = (t) => USER_TYPES.find(x => x.value === t) || USER_TYPES[0];

const ALL_ROLES = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-purple-100 text-purple-700', desc: 'Full platform access' },
  { value: 'membership_lead', label: 'Membership Team Lead', color: 'bg-blue-100 text-blue-700', desc: 'Membership plans, members, support' },
  { value: 'membership_support', label: 'Membership Support', color: 'bg-blue-50 text-blue-600', desc: 'Member support and basic management' },
  { value: 'marketing_team', label: 'Marketing Team', color: 'bg-green-100 text-green-700', desc: 'Blog, social requests, marketing' },
  { value: 'events_manager', label: 'Events Team', color: 'bg-orange-100 text-orange-700', desc: 'Events and registrations' },
  { value: 'blog_reviewer', label: 'Blog Reviewer / Content', color: 'bg-indigo-100 text-indigo-700', desc: 'Blog review and approval' },
  { value: 'committee_manager', label: 'Committee Leader', color: 'bg-teal-100 text-teal-700', desc: 'Committee management' },
  { value: 'member', label: 'Member User', color: 'bg-slate-100 text-slate-700', desc: 'Member portal access' },
];

const ROLE_MODULES = {
  super_admin: ['All Modules'],
  membership_lead: ['dashboard', 'members', 'membership_plans', 'announcements', 'support_inbox', 'applications'],
  membership_support: ['dashboard', 'members', 'membership_plans', 'announcements', 'support_inbox', 'applications'],
  marketing_team: ['dashboard', 'blog_news', 'blog_review', 'social_requests', 'marketing_requests', 'brand_requests'],
  events_manager: ['dashboard', 'events'],
  blog_reviewer: ['dashboard', 'blog_news', 'blog_review'],
  committee_manager: ['dashboard', 'committees'],
  member: ['portal_community', 'portal_home', 'portal_events', 'portal_committees', 'portal_requests', 'portal_blog', 'portal_social', 'portal_profile', 'portal_support'],
};

// Staff modules
const STAFF_MODULES = [
  { key: 'dashboard', label: 'Dashboard', section: 'Core' },
  { key: 'members', label: 'Members', section: 'Membership' },
  { key: 'applications', label: 'Applications', section: 'Membership' },
  { key: 'membership_plans', label: 'Membership Plans', section: 'Membership' },
  { key: 'announcements', label: 'Announcements', section: 'Membership' },
  { key: 'support_inbox', label: 'Support Inbox', section: 'Membership' },
  { key: 'company_profiles', label: 'Company Profiles', section: 'Membership' },
  { key: 'events', label: 'Calendar / Events', section: 'Operations' },
  { key: 'committees', label: 'Committees', section: 'Operations' },
  { key: 'community_moderation', label: 'Community Moderation', section: 'Operations' },
  { key: 'blog_news', label: 'Blog & News', section: 'Content' },
  { key: 'blog_review', label: 'Blog Reviews', section: 'Content' },
  { key: 'social_requests', label: 'Social Media Requests', section: 'Marketing' },
  { key: 'marketing_requests', label: 'Marketing / Flyer Requests', section: 'Marketing' },
  { key: 'brand_requests', label: 'Brand Requests', section: 'Marketing' },
  { key: 'public_visibility', label: 'Public Visibility Controls', section: 'Settings' },
  { key: 'user_management', label: 'User Management', section: 'Settings' },
];

// Member portal modules
const MEMBER_MODULES = [
  { key: 'portal_community', label: 'Home (Community Feed)', section: 'Portal' },
  { key: 'portal_home', label: 'Dashboard', section: 'Portal' },
  { key: 'portal_events', label: 'Events', section: 'Portal' },
  { key: 'portal_committees', label: 'Committees', section: 'Portal' },
  { key: 'portal_requests', label: 'Requests', section: 'Portal' },
  { key: 'portal_blog', label: 'Blog Submission', section: 'Portal' },
  { key: 'portal_social', label: 'Social Media Request', section: 'Portal' },
  { key: 'portal_profile', label: 'Company Profile', section: 'Portal' },
  { key: 'portal_support', label: 'Membership Support', section: 'Portal' },
];

const getModulesForRole = (role) => role === 'member' ? MEMBER_MODULES : STAFF_MODULES;
const getSectionsForRole = (role) => role === 'member' ? ['Portal'] : ['Core', 'Membership', 'Content', 'Marketing', 'Operations', 'Settings'];

const getDefaultPerms = (role) => {
  const defaults = ROLE_MODULES[role] || [];
  if (defaults[0] === 'All Modules') return null; // super_admin
  const modules = getModulesForRole(role);
  const perms = {};
  modules.forEach(m => { perms[m.key] = defaults.includes(m.key); });
  return perms;
};

const getRoleInfo = (role) => ALL_ROLES.find(r => r.value === role) || ALL_ROLES[ALL_ROLES.length - 1];

// --- Password-reset UI helpers (used by the secure reset modals) -------------
const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const StatusBadge = ({ status }) => {
  const cfg = {
    sent:      { bg: 'bg-sky-100',     fg: 'text-sky-700',     label: 'Sent' },
    used:      { bg: 'bg-emerald-100', fg: 'text-emerald-700', label: 'Used' },
    expired:   { bg: 'bg-slate-100',   fg: 'text-slate-500',   label: 'Expired' },
    cancelled: { bg: 'bg-amber-100',   fg: 'text-amber-700',   label: 'Cancelled' },
  }[status] || { bg: 'bg-slate-100', fg: 'text-slate-500', label: status };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${cfg.bg} ${cfg.fg}`}>
      {cfg.label}
    </span>
  );
};

const ShieldIcon = () => <Shield className="w-3.5 h-3.5 text-emerald-500 shrink-0" />;
// -----------------------------------------------------------------------------

const UsersManagementPage = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');  // NEW — category layer
  const [showCreate, setShowCreate] = useState(false);
  const [createMode, setCreateMode] = useState('staff');
  const [editUser, setEditUser] = useState(null);
  const [showPassword, setShowPassword] = useState({});
  // resetResult is the secure "reset email sent" modal — never carries a password.
  const [resetResult, setResetResult] = useState(null);
  const [resetHistoryUser, setResetHistoryUser] = useState(null);
  const [resetHistory, setResetHistory] = useState([]);
  const [resetHistoryLoading, setResetHistoryLoading] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [createResult, setCreateResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permData, setPermData] = useState({});
  const [adminAccessUser, setAdminAccessUser] = useState(null);
  const [adminAccessData, setAdminAccessData] = useState({ admin_access: false, admin_modules: {}, activity_visibility_granted: false });
  const [emailHistoryUser, setEmailHistoryUser] = useState(null);
  const [changeEmailUser, setChangeEmailUser] = useState(null);

  const [newUser, setNewUser] = useState({
    name: '', email: '', password: '', role: 'member', phone: '',
    company_name: '', plan_id: '', is_active: true,
  });

  // Lock body scroll while ANY modal is open
  useBodyScrollLock(
    !!showCreate || !!editUser || !!resetResult || !!deleteConfirm ||
    !!createResult || !!permUser || !!adminAccessUser || !!resetHistoryUser || !!emailHistoryUser || !!changeEmailUser
  );

  useEffect(() => { fetchData(); }, []);  

  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [usersRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/membership-plans/all`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data);
      setPlans(plansRes.data);
    } catch { /* */ } finally { setLoading(false); }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData({ silent: true });
      toast.success('User list refreshed');
    } catch { /* */ } finally { setRefreshing(false); }
  };

  const handleCreate = async () => {
    if (!newUser.name.trim() || !newUser.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/users`, newUser, { withCredentials: true });
      toast.success('User created!');
      setCreateResult(res.data);
      setNewUser({ name: '', email: '', password: '', role: 'member', phone: '', company_name: '', plan_id: '', is_active: true });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const payload = {
        name: editUser.name,
        role: editUser.role,
        phone: editUser.phone,
        is_active: editUser.is_active !== false,
        user_type: editUser.user_type || 'member',
        membership_tier: editUser.membership_tier,
        assigned_plan_id: editUser.assigned_plan_id,
      };
      await axios.put(`${API}/admin/users/${editUser.id}`, payload, { withCredentials: true });
      toast.success('User updated!');
      setEditUser(null);
      fetchData();
    } catch (err) { toast.error('Failed to update'); } finally { setSaving(false); }
  };

  const handleToggleActive = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/toggle-active`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const handleToggleBadge = async (user) => {
    const current = user.badges || [];
    const has = current.includes('founding_member');
    const newBadges = has ? current.filter(b => b !== 'founding_member') : [...current, 'founding_member'];
    try {
      await axios.put(`${API}/admin/users/${user.id}/badges`, { badges: newBadges }, { withCredentials: true });
      toast.success(has ? 'Founding Member badge removed' : 'Founding Member badge assigned!');
      fetchData();
    } catch (err) { toast.error('Failed to update badge'); }
  };

  // Password reset trigger.
  //   - Default (all admins): backend sends the reset email, returns confirmation
  //     only. Admin does NOT see the token.
  //   - Super admin escape hatch: we pass `include_link: true` so the backend
  //     ALSO returns the plaintext `reset_link` inline. This is critical while
  //     Resend email delivery is still being finalized in production — super
  //     admins can copy the link and hand-deliver via WhatsApp / SMS / phone.
  //     Non-super admins passing `include_link` get it stripped server-side.
  const handleSendResetEmail = async (user) => {
    if (user.pending_activation) {
      toast.error('This member has not activated yet. Send an invitation instead.');
      return;
    }
    setResetSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/users/${user.id}/send-password-reset`,
        { include_link: isSuperAdmin },
        { withCredentials: true }
      );
      setResetResult({
        userId: user.id,
        name: user.name,
        email: user.email,
        expires_in_minutes: res.data?.expires_in_minutes || 60,
        email_delivered: res.data?.email_delivered !== false,
        // Only super_admins receive `reset_link` from the backend. Guard just
        // in case a hostile response tries to leak the field through — defense
        // in depth against a compromised admin.
        reset_link: (isSuperAdmin && res.data?.reset_link) ? res.data.reset_link : null,
      });
      toast.success(res.data?.message || 'Password reset email sent.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setResetSending(false);
    }
  };

  const openResetHistory = async (user) => {
    setResetHistoryUser(user);
    setResetHistory([]);
    setResetHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/users/${user.id}/password-reset-history`,
        { withCredentials: true }
      );
      setResetHistory(res.data?.history || []);
    } catch {
      toast.error('Could not load reset history');
    } finally {
      setResetHistoryLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const res = await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success(res.data.message);
      setDeleteConfirm(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleAssignPlan = async (userId, planId) => {
    try {
      await axios.put(`${API}/admin/members/${userId}/plan?plan_id=${planId}`, {}, { withCredentials: true });
      toast.success('Plan assigned!');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to assign plan'); }
  };

  const openPermissions = (user) => {
    const modules = getModulesForRole(user.role);
    const existing = user.module_permissions;
    if (existing && typeof existing === 'object' && Object.keys(existing).length > 0) {
      // Ensure all modules for this role type are present
      const merged = {};
      modules.forEach(m => { merged[m.key] = existing[m.key] !== undefined ? existing[m.key] : true; });
      setPermData(merged);
    } else {
      setPermData(getDefaultPerms(user.role) || {});
    }
    setPermUser(user);
  };

  const handleSavePermissions = async () => {
    if (!permUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${permUser.id}/permissions`, { module_permissions: permData }, { withCredentials: true });
      toast.success('Permissions saved! Changes take effect on next login.');
      setPermUser(null);
      fetchData();
    } catch (err) { toast.error('Failed to save permissions'); } finally { setSaving(false); }
  };

  const togglePerm = (key) => {
    setPermData(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const setAllPerms = (val) => {
    if (!permUser) return;
    const modules = getModulesForRole(permUser.role);
    const newPerms = {};
    modules.forEach(m => { newPerms[m.key] = val; });
    setPermData(newPerms);
  };

  // Admin Access Control for Leadership users
  const ADMIN_MODULES_LIST = [
    { key: 'dashboard', label: 'Executive Dashboard', section: 'Operations' },
    { key: 'events', label: 'Events Management', section: 'Operations' },
    { key: 'community_moderation', label: 'Community Moderation', section: 'Operations' },
    { key: 'committees', label: 'Committees', section: 'Operations' },
    { key: 'members', label: 'Members Management', section: 'Membership' },
    { key: 'member_center', label: 'Member Center', section: 'Membership' },
    { key: 'newsletter_center', label: 'Newsletter & Communications', section: 'Membership' },
    { key: 'email_communications', label: 'Email Communications (AI)', section: 'Membership' },
    { key: 'applications', label: 'Applications', section: 'Membership' },
    { key: 'membership_plans', label: 'Membership Plans', section: 'Membership' },
    { key: 'announcements', label: 'Announcements', section: 'Membership' },
    { key: 'support_inbox', label: 'Support Inbox', section: 'Membership' },
    { key: 'blog_news', label: 'Blog & News', section: 'Content' },
    { key: 'blog_review', label: 'Blog Reviews', section: 'Content' },
    { key: 'social_requests', label: 'Social Media Requests', section: 'Marketing' },
    { key: 'marketing_requests', label: 'Marketing Requests', section: 'Marketing' },
    { key: 'brand_requests', label: 'Brand Requests', section: 'Marketing' },
    { key: 'user_management', label: 'Board Dashboard & Settings', section: 'Settings' },
    { key: 'platform_settings', label: 'Platform Settings & Welcome Experience', section: 'Settings' },
  ];

  const openAdminAccess = (user) => {
    setAdminAccessData({
      admin_access: user.admin_access || false,
      admin_modules: user.admin_modules || {},
      activity_visibility_granted: !!user.activity_visibility_granted,
    });
    setAdminAccessUser(user);
  };

  const handleSaveAdminAccess = async () => {
    if (!adminAccessUser) return;
    setSaving(true);
    try {
      const { activity_visibility_granted, ...rest } = adminAccessData;
      await axios.put(`${API}/admin/users/${adminAccessUser.id}/admin-access`, rest, { withCredentials: true });
      // Sync activity visibility independently (super-admin only endpoint)
      try {
        await axios.put(
          `${API}/admin/users/${adminAccessUser.id}/activity-access`,
          { granted: activity_visibility_granted },
          { withCredentials: true }
        );
      } catch (actErr) {
        // Non-super-admins can still save admin access — don't block
        if (actErr?.response?.status !== 403) throw actErr;
      }
      toast.success('Access updated! Changes take effect on next login.');
      setAdminAccessUser(null);
      fetchData();
    } catch (err) { toast.error('Failed to save admin access'); } finally { setSaving(false); }
  };

  const toggleAdminModule = (key) => {
    setAdminAccessData(prev => ({
      ...prev,
      admin_modules: { ...prev.admin_modules, [key]: !prev.admin_modules[key] }
    }));
  };

  const setAllAdminModules = (val) => {
    const newMods = {};
    ADMIN_MODULES_LIST.forEach(m => { newMods[m.key] = val; });
    setAdminAccessData(prev => ({ ...prev, admin_modules: newMods }));
  };

  const filtered = users.filter(u => {
    // User Type filter (new category layer)
    if (userTypeFilter !== 'all' && (u.user_type || 'member') !== userTypeFilter) return false;
    // Role filter
    if (roleFilter !== 'all') {
      if (roleFilter === 'staff') {
        if (u.role === 'member') return false;
      } else if (u.role !== roleFilter) return false;
    }
    // Search
    if (search) {
      const s = search.toLowerCase();
      return (u.name || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s) || (u.company_name || '').toLowerCase().includes(s);
    }
    return true;
  });

  // Counts per user type for filter-tab badges
  const typeCounts = USER_TYPES.reduce((acc, t) => {
    acc[t.value] = users.filter(u => (u.user_type || 'member') === t.value).length;
    return acc;
  }, {});

  const inputClass = "w-full border border-slate-200 rounded-sm p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]";

  return (
    <div className="p-6 lg:p-8" data-testid="users-management">
      {/* Delete Confirmation Modal */}
      <ModalPortal>
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
            <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="delete-user-modal">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
                <h3 className="text-lg font-bold text-slate-900">Delete User?</h3>
              </div>
              <p className="text-sm text-slate-600 mb-1">Are you sure you want to permanently delete:</p>
              <p className="text-sm font-semibold text-slate-900 mb-4">{deleteConfirm.name} ({deleteConfirm.email})</p>
              <p className="text-xs text-red-500 mb-5">This will remove all their data: posts, comments, messages, profile, etc.</p>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="delete-cancel">Cancel</button>
                <button onClick={() => handleDeleteUser(deleteConfirm.id)} className="flex-1 py-2.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700" data-testid="delete-confirm">Delete</button>
              </div>
            </div>
          </div>
        )}
      </ModalPortal>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#002249]">User Management</h1>
          <p className="text-slate-500 text-[13px] mt-1">{users.length} total users · Admin controls roles, types, and module access manually</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-sm h-10"
            data-testid="refresh-users-btn"
            title="Reload user list"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button onClick={() => { setShowCreate(true); setCreateMode('member'); setCreateResult(null); setNewUser({...newUser, role: 'member'}); }} size="sm" className="bg-[#E63946] hover:bg-[#d32f3f] rounded-sm h-10" data-testid="new-member-btn">
            <UserPlus className="w-4 h-4 mr-1.5" /><span className="hidden xs:inline">New </span>Member
          </Button>
          <Button onClick={() => { setShowCreate(true); setCreateMode('staff'); setCreateResult(null); setNewUser({...newUser, role: 'membership_lead'}); }} size="sm" variant="outline" className="rounded-sm h-10" data-testid="new-user-btn">
            <Plus className="w-4 h-4 mr-1.5" /><span className="hidden xs:inline">New </span>Staff
          </Button>
        </div>
      </div>

      {/* User Type Filter Tabs */}
      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto scrollbar-hide pb-1" data-testid="user-type-tabs">
        <button
          onClick={() => setUserTypeFilter('all')}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-[12px] font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${userTypeFilter === 'all' ? 'bg-[#002249] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 border border-slate-200 bg-white'}`}
          data-testid="user-type-tab-all"
        >
          <Users className="w-3.5 h-3.5" />All<span className={`ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${userTypeFilter === 'all' ? 'bg-white/20' : 'bg-slate-100'}`}>{users.length}</span>
        </button>
        {USER_TYPES.map(t => {
          const Icon = t.icon;
          const active = userTypeFilter === t.value;
          return (
            <button key={t.value} onClick={() => setUserTypeFilter(t.value)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-[12px] font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${active ? 'bg-[#002249] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100 border border-slate-200 bg-white'}`}
              data-testid={`user-type-tab-${t.value}`}
            >
              <Icon className="w-3.5 h-3.5" />{t.label}s<span className={`ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${active ? 'bg-white/20' : 'bg-slate-100'}`}>{typeCounts[t.value] || 0}</span>
            </button>
          );
        })}
      </div>

      {/* Search & Role Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or company..." className="pl-9 rounded-sm h-10" data-testid="search-users" />
        </div>
        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="border border-slate-200 rounded-sm px-3 h-10 text-[13px] bg-white font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
          data-testid="role-filter-select"
        >
          <option value="all">All Roles</option>
          <option value="staff">Staff (non-member)</option>
          {ALL_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wide">User</th>
                  <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wide hidden sm:table-cell">Type</th>
                  <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wide hidden md:table-cell">Role</th>
                  <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wide hidden lg:table-cell">Plan</th>
                  <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wide hidden xl:table-cell">Status</th>
                  <th className="text-right p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((user, index) => {
                  const roleInfo = getRoleInfo(user.role);
                  const typeInfo = getUserTypeInfo(user.user_type || 'member');
                  const TypeIcon = typeInfo.icon;
                  const isActive = user.is_active !== false;
                  return (
                    <tr key={user.id} className={`hover:bg-slate-50 ${!isActive ? 'opacity-50' : ''}`} data-testid={`user-row-${index}`}>
                      <td className="p-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${isActive ? 'bg-[#002249]' : 'bg-slate-300'}`}>
                            {user.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-medium text-sm text-slate-900 truncate">{user.name}</p>
                              {user.badges?.includes('founding_member') && <span className="text-[9px] font-bold bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full whitespace-nowrap" data-testid={`founding-badge-${index}`}>Founding</span>}
                              {user.admin_access && <span className="text-[9px] font-bold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">Admin Access</span>}
                            </div>
                            <p className="text-xs text-slate-500 truncate">{user.email}</p>
                            {user.company_name && <p className="text-[10px] text-slate-400 truncate">{user.company_name}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 hidden sm:table-cell">
                        <span className={`text-[11px] font-semibold px-2 py-1 rounded-sm inline-flex items-center gap-1 ${typeInfo.color}`} data-testid={`user-type-badge-${index}`}>
                          <TypeIcon className="w-3 h-3" />{typeInfo.label}
                        </span>
                      </td>
                      <td className="p-3 hidden md:table-cell">
                        <span className={`text-[11px] font-semibold px-2 py-1 rounded-sm ${roleInfo.color}`}>{roleInfo.label}</span>
                      </td>
                      <td className="p-3 hidden lg:table-cell">
                        {user.role === 'member' ? (
                          <select
                            value={user.membership_tier || user.assigned_plan_id || ''}
                            onChange={async (e) => {
                              try {
                                await axios.put(`${API}/admin/users/${user.id}`, { assigned_plan_id: e.target.value, membership_tier: e.target.value }, { withCredentials: true });
                                toast.success('Plan updated');
                                fetchData();
                              } catch { toast.error('Failed'); }
                            }}
                            className="text-[11px] border border-slate-200 rounded-sm px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#002249]/20 w-full max-w-[140px]"
                            data-testid={`plan-select-${index}`}
                          >
                            <option value="">No plan</option>
                            {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        ) : (
                          <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">—</span>
                        )}
                      </td>
                      <td className="p-3 hidden xl:table-cell">
                        {isActive ? (
                          <span className="text-xs text-green-600 flex items-center"><CheckCircle2 className="w-3 h-3 mr-1" />Active</span>
                        ) : (
                          <span className="text-xs text-red-600 flex items-center"><XCircle className="w-3 h-3 mr-1" />Inactive</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end items-center gap-0.5">
                          <Button size="sm" variant="ghost" onClick={() => setEditUser({...user})} title="Edit user details" className="h-8 w-8 p-0" data-testid={`edit-user-${index}`}><Edit2 className="w-4 h-4 text-slate-500" /></Button>
                          {/* Manage Access — available for ALL users, not just leadership. Admin controls permissions manually. */}
                          {user.role !== 'super_admin' && (
                            <Button size="sm" variant="ghost" onClick={() => openAdminAccess(user)} title="Manage Access (admin portal + modules)" className="h-8 w-8 p-0" data-testid={`admin-access-${index}`}>
                              <Shield className={`w-4 h-4 ${user.admin_access ? 'text-purple-600' : 'text-slate-400'}`} />
                            </Button>
                          )}
                          {user.role === 'member' && (
                            <Button size="sm" variant="ghost" onClick={() => handleToggleBadge(user)} title={user.badges?.includes('founding_member') ? 'Remove Founding Member' : 'Set Founding Member'} className="h-8 w-8 p-0" data-testid={`badge-user-${index}`}>
                              <Award className={`w-4 h-4 ${user.badges?.includes('founding_member') ? 'text-amber-500' : 'text-slate-300'}`} />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleToggleActive(user.id)} title={isActive ? 'Deactivate' : 'Activate'} className="h-8 w-8 p-0" data-testid={`toggle-user-${index}`}>
                            <Power className={`w-4 h-4 ${isActive ? 'text-emerald-500' : 'text-rose-400'}`} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleSendResetEmail(user)} disabled={resetSending} title="Send Password Reset Email" className="h-8 w-8 p-0" data-testid={`send-reset-${index}`}><Key className="w-4 h-4 text-amber-500" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => openResetHistory(user)} title="Password Reset History" className="h-8 w-8 p-0" data-testid={`reset-history-${index}`}><History className="w-4 h-4 text-slate-400" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setEmailHistoryUser(user)} title="Email Communications History" className="h-8 w-8 p-0" data-testid={`email-history-${index}`}><Mail className="w-4 h-4 text-sky-500" /></Button>
                          {isSuperAdmin && user.role !== 'super_admin' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                const next = !user.ai_agent_access;
                                try {
                                  await axios.patch(`${API}/admin/users/${user.id}/ai-agent-access`, { enabled: next }, { withCredentials: true });
                                  toast.success(`AI Agent ${next ? 'enabled' : 'disabled'} for ${user.name}`);
                                  fetchData({ silent: true });
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Could not update AI access');
                                }
                              }}
                              title={user.ai_agent_access ? 'Revoke AI Agent access' : 'Grant AI Agent access'}
                              className="h-8 w-8 p-0"
                              data-testid={`ai-access-${index}`}
                            >
                              <Bot className={`w-4 h-4 ${user.ai_agent_access ? 'text-emerald-500' : 'text-slate-300'}`} />
                            </Button>
                          )}
                          {isSuperAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => setChangeEmailUser(user)} title="Change account email" className="h-8 w-8 p-0" data-testid={`change-email-${index}`}>
                              <AtSign className="w-4 h-4 text-indigo-500" />
                            </Button>
                          )}
                          {user.role !== 'super_admin' && (
                            <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(user)} title="Delete User" className="h-8 w-8 p-0" data-testid={`delete-user-${index}`}>
                              <Trash2 className="w-4 h-4 text-rose-400 hover:text-rose-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="p-12 text-center text-slate-400"><UserCog className="w-12 h-12 mx-auto mb-2 text-slate-300" /><p>No users found</p></div>}
          </div>
        </div>
      )}

      {/* Password Reset — secure confirmation (no password ever displayed) */}
      <ModalPortal>
        {resetResult && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResetResult(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={e => e.stopPropagation()}
              data-testid="reset-sent-modal"
            >
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-5 text-center rounded-t-2xl">
                <Send className="w-10 h-10 text-white mx-auto mb-2" />
                <h3 className="font-bold text-white text-lg">Reset email sent</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="text-sm text-slate-700 leading-relaxed">
                  We've sent a secure password reset link to{' '}
                  <strong className="text-[#002249]">{resetResult.email}</strong>.
                  The link expires in <strong>{resetResult.expires_in_minutes} minutes</strong> and can only be used once.
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1.5">
                  <div className="flex items-center gap-2"><ShieldIcon /> <span>You don't see the password — only {resetResult.name} can set the new one.</span></div>
                  <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-amber-500 shrink-0" /> <span>Link expires in {resetResult.expires_in_minutes} min</span></div>
                </div>
                {resetResult.email_delivered === false && (
                  <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>Email delivery may have failed. Check the user's reset history or resend.</span>
                  </div>
                )}

                {/* SUPER-ADMIN ONLY: copyable reset link (backup delivery via
                    WhatsApp/SMS while Resend is being finalized). Only rendered
                    when the backend actually returned `reset_link`. */}
                {resetResult.reset_link && (
                  <div
                    className="border border-emerald-200 bg-emerald-50/60 rounded-lg p-4 space-y-3"
                    data-testid="reset-link-panel"
                  >
                    <div className="flex items-center gap-2">
                      <Send className="w-4 h-4 text-emerald-600 shrink-0" />
                      <div className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                        Direct reset link (super admin)
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Since email delivery is still being finalized, you can copy
                      this link and hand-deliver it to <strong>{resetResult.name}</strong> via
                      WhatsApp, SMS, or phone. The link expires in{' '}
                      <strong>{resetResult.expires_in_minutes} minutes</strong>.
                    </p>
                    <div className="flex items-stretch gap-2">
                      <input
                        type="text"
                        readOnly
                        value={resetResult.reset_link}
                        onFocus={(e) => e.currentTarget.select()}
                        className="flex-1 min-w-0 text-[11px] font-mono bg-white border border-slate-300 rounded-md px-2.5 py-2 truncate focus:outline-none focus:border-emerald-500"
                        data-testid="reset-link-input"
                      />
                      <Button
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(resetResult.reset_link);
                            toast.success('Reset link copied to clipboard');
                          } catch {
                            toast.error('Copy failed — select the link and copy manually');
                          }
                        }}
                        className="shrink-0 bg-emerald-600 hover:bg-emerald-700 rounded-md text-xs px-3"
                        data-testid="reset-link-copy"
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <p className="text-[10.5px] text-slate-500 leading-snug">
                      🔒 Every time you view this link, it's recorded in the audit log.
                      Do not share it via unsecured channels. Never post publicly.
                    </p>
                  </div>
                )}

                <Button
                  onClick={() => setResetResult(null)}
                  className="w-full mt-1 bg-[#002249] hover:bg-[#0a3570] rounded-lg"
                  data-testid="reset-sent-done"
                >
                  Done
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </ModalPortal>

      {/* Email Communications History (Phase 3) */}
      <ModalPortal>
        {emailHistoryUser && (
          <MemberEmailHistoryModal
            user={emailHistoryUser}
            onClose={() => setEmailHistoryUser(null)}
          />
        )}
      </ModalPortal>

      {/* Change Account Email (Super Admin only) */}
      {changeEmailUser && (
        <ChangeEmailModal
          user={changeEmailUser}
          onClose={() => setChangeEmailUser(null)}
          onSaved={(data) => {
            // Mutate the in-memory list so the row reflects the new email
            // without needing a full refetch.
            setUsers((prev) => prev.map((u) =>
              u.id === data.user_id ? { ...u, email: data.email } : u,
            ));
          }}
        />
      )}


      {/* Password Reset History */}
      <ModalPortal>
        {resetHistoryUser && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResetHistoryUser(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl max-w-xl w-full max-h-[88vh] overflow-hidden shadow-2xl flex flex-col"
              onClick={e => e.stopPropagation()}
              data-testid="reset-history-modal"
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-[#002249] text-lg">Password reset history</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{resetHistoryUser.name} · {resetHistoryUser.email}</p>
                </div>
                <button onClick={() => setResetHistoryUser(null)} className="text-slate-400 hover:text-slate-700" data-testid="reset-history-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-slate-500">Most recent first · {resetHistory.length} entr{resetHistory.length === 1 ? 'y' : 'ies'}</p>
                  <button
                    onClick={() => { handleSendResetEmail(resetHistoryUser); }}
                    disabled={resetSending || resetHistoryUser.pending_activation}
                    className="text-xs font-semibold text-[#E63946] hover:underline disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="resend-from-history"
                  >
                    <Send className="w-3 h-3" /> Resend reset email
                  </button>
                </div>
                {resetHistoryLoading ? (
                  <div className="text-center py-10 text-slate-400 text-sm">Loading...</div>
                ) : resetHistory.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">
                    <History className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    No password resets have been issued for this account.
                  </div>
                ) : (
                  <ul className="space-y-2.5" data-testid="reset-history-list">
                    {resetHistory.map((row) => (
                      <li key={row.id} className="border border-slate-200 rounded-lg p-3.5 hover:bg-slate-50 transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <StatusBadge status={row.status} />
                              <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                                {row.source === 'admin' ? 'Admin initiated' : 'Member self-service'}
                              </span>
                            </div>
                            <div className="text-xs text-slate-600 mt-1.5 space-y-0.5">
                              <div><strong>Sent:</strong> {formatTimestamp(row.sent_at)}</div>
                              {row.used_at && <div><strong>Used:</strong> {formatTimestamp(row.used_at)}</div>}
                              {row.cancelled_at && <div><strong>Cancelled:</strong> {formatTimestamp(row.cancelled_at)}</div>}
                              {row.status === 'sent' && row.expires_at && <div><strong>Expires:</strong> {formatTimestamp(row.expires_at)}</div>}
                              {row.initiated_by_name && row.source === 'admin' && (
                                <div className="text-slate-500"><strong>By:</strong> {row.initiated_by_name}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </ModalPortal>

      {/* Create User Modal */}
      <ModalPortal>
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowCreate(false); setCreateResult(null); }}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-sm max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-user-modal">
              <div className="p-5 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-bold text-[#002249]">{createMode === 'member' ? 'Add New Member' : 'Create Staff User'}</h2>
                <button onClick={() => { setShowCreate(false); setCreateResult(null); }}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              {createResult ? (
                <div className="p-5" data-testid="create-result">
                  <div className="text-center mb-4">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <h3 className="font-bold text-[#002249]">User Created!</h3>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-2 text-sm">
                    <p><span className="font-medium text-slate-600">Name:</span> {createResult.name}</p>
                    <p><span className="font-medium text-slate-600">Email:</span> {createResult.email}</p>
                    <p><span className="font-medium text-slate-600">Role:</span> {getRoleInfo(createResult.role).label}</p>
                    <div>
                      <span className="font-medium text-slate-600">Password: </span>
                      <code className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded text-xs">{createResult.temp_password}</code>
                      <button onClick={() => { navigator.clipboard.writeText(createResult.temp_password); toast.success('Copied!'); }} className="ml-2 text-[#002249]"><Copy className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-3">Share these credentials with the user securely. They can change their password after login.</p>
                  <div className="flex space-x-3 mt-4">
                    <Button onClick={() => setCreateResult(null)} variant="outline" className="flex-1 rounded-sm">Create Another</Button>
                    <Button onClick={() => { setShowCreate(false); setCreateResult(null); }} className="flex-1 bg-[#002249] rounded-sm">Done</Button>
                  </div>
                </div>
              ) : (
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Full Name *</Label><input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className={inputClass} placeholder="John Doe" data-testid="new-user-name" /></div>
                    <div><Label className="text-xs">Email *</Label><input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className={inputClass} placeholder="john@company.com" data-testid="new-user-email" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Phone</Label><input value={newUser.phone} onChange={e => setNewUser({...newUser, phone: e.target.value})} className={inputClass} placeholder="+1 (xxx) xxx-xxxx" data-testid="new-user-phone" /></div>
                    <div>
                      <Label className="text-xs">Password (auto-generated if empty)</Label>
                      <input value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} className={inputClass} placeholder="Leave blank for auto" data-testid="new-user-password" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Role *</Label>
                    <select value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})} className={inputClass} data-testid="new-user-role">
                      {ALL_ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                    </select>
                  </div>
                  {/* Module access preview */}
                  <div className="bg-purple-50 border border-purple-200 rounded-sm p-3" data-testid="create-user-access-hint">
                    <div className="flex items-start gap-2">
                      <Shield className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <p className="text-[11px] text-purple-800 leading-relaxed">
                        Permissions are <strong>fully manual</strong>. After creating this user, click the <strong>shield</strong> icon next to their row to toggle admin access and module permissions.
                      </p>
                    </div>
                  </div>
                  {(newUser.role === 'member' || createMode === 'member') && (
                    <>
                      <div><Label className="text-xs">Company Name</Label><input value={newUser.company_name} onChange={e => setNewUser({...newUser, company_name: e.target.value})} className={inputClass} placeholder="Company LLC" data-testid="new-user-company" /></div>
                      <div>
                        <Label className="text-xs">Assign Membership Plan</Label>
                        <select value={newUser.plan_id} onChange={e => setNewUser({...newUser, plan_id: e.target.value})} className={inputClass} data-testid="new-user-plan">
                          <option value="">No plan assigned</option>
                          {plans.filter(p => p.is_active).map(p => <option key={p.id} value={p.id}>{p.name} — {p.price}</option>)}
                        </select>
                      </div>
                    </>
                  )}
                  <div className="flex space-x-3 pt-2">
                    <Button variant="outline" onClick={() => { setShowCreate(false); setCreateResult(null); }} className="flex-1 rounded-sm">Cancel</Button>
                    <Button onClick={handleCreate} disabled={saving} className="flex-1 bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="create-user-submit">
                      {saving ? 'Creating...' : createMode === 'member' ? 'Enroll Member' : 'Create User'}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </ModalPortal>

      {/* Edit User Modal */}
      <ModalPortal>
        {editUser && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditUser(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-sm max-w-lg w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="edit-user-modal">
              <div className="p-4 sm:p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                <h2 className="text-base sm:text-lg font-bold text-[#002249]">Edit User</h2>
                <button onClick={() => setEditUser(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <div className="p-4 sm:p-5 space-y-4">
                <div><Label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Name</Label><input value={editUser.name || ''} onChange={e => setEditUser({...editUser, name: e.target.value})} className={inputClass} data-testid="edit-user-name" /></div>
                <div><Label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Email (read-only)</Label><input value={editUser.email || ''} disabled className={`${inputClass} bg-slate-50 text-slate-500`} /></div>
                <div><Label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Phone</Label><input value={editUser.phone || ''} onChange={e => setEditUser({...editUser, phone: e.target.value})} className={inputClass} /></div>

                {/* USER TYPE — new category layer */}
                <div>
                  <Label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">User Type <span className="text-slate-400 normal-case font-normal">(category)</span></Label>
                  <select value={editUser.user_type || 'member'} onChange={e => setEditUser({...editUser, user_type: e.target.value})} className={inputClass} data-testid="edit-user-type">
                    {USER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>)}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">User Type is a <strong>category label</strong>, independent of role. Permissions are managed separately below.</p>
                </div>

                {/* ROLE — label only */}
                <div>
                  <Label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Role <span className="text-slate-400 normal-case font-normal">(label)</span></Label>
                  <select value={editUser.role || 'member'} onChange={e => setEditUser({...editUser, role: e.target.value})} className={inputClass} data-testid="edit-user-role">
                    {ALL_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>

                {/* STATUS */}
                <div>
                  <Label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Status</Label>
                  <select value={editUser.is_active === false ? 'inactive' : 'active'} onChange={e => setEditUser({...editUser, is_active: e.target.value === 'active'})} className={inputClass} data-testid="edit-user-status">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {editUser.role === 'member' && (
                  <div>
                    <Label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Membership Plan</Label>
                    <select value={editUser.assigned_plan_id || editUser.membership_tier || ''} onChange={e => setEditUser({...editUser, assigned_plan_id: e.target.value, membership_tier: e.target.value})} className={inputClass} data-testid="edit-user-plan">
                      <option value="">No plan assigned</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.name} — {p.price}</option>)}
                    </select>
                  </div>
                )}

                {/* MANAGE ACCESS SHORTCUT — replaces the old confusing auto-permissions hint */}
                <div className="bg-purple-50 border border-purple-200 rounded-sm p-3.5">
                  <div className="flex items-start gap-2.5">
                    <Shield className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-purple-900">Permissions</p>
                      <p className="text-[11px] text-purple-700 mt-0.5 leading-relaxed">
                        Admin portal access and module access are <strong>fully manual</strong>. Role & User Type do not auto-grant permissions.
                      </p>
                      <Button
                        size="sm"
                        onClick={() => {
                          const u = editUser;
                          setEditUser(null);
                          setTimeout(() => openAdminAccess(u), 50);
                        }}
                        className="mt-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-sm h-8 text-[12px]"
                        data-testid="edit-manage-access-btn"
                      >
                        <Settings className="w-3.5 h-3.5 mr-1.5" />Manage Access
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setEditUser(null)} className="flex-1 rounded-sm">Cancel</Button>
                  <Button onClick={handleUpdate} disabled={saving} className="flex-1 bg-[#002249] rounded-sm" data-testid="save-user-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </ModalPortal>

      {/* Permissions Modal — Real ON/OFF toggle switches */}
      <ModalPortal>
        {permUser && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPermUser(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-sm max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="permissions-modal">
              <div className="p-5 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-[#002249]">Module Permissions</h2>
                    <p className="text-sm text-slate-500 mt-0.5">{permUser.name} ({getRoleInfo(permUser.role).label})</p>
                  </div>
                  <button onClick={() => setPermUser(null)}><X className="w-5 h-5 text-slate-400" /></button>
                </div>
                <div className="flex space-x-2 mt-3">
                  <button onClick={() => setAllPerms(true)} className="text-xs text-green-600 font-medium hover:underline" data-testid="enable-all">Enable All</button>
                  <span className="text-slate-300">|</span>
                  <button onClick={() => setAllPerms(false)} className="text-xs text-red-600 font-medium hover:underline" data-testid="disable-all">Disable All</button>
                  <span className="text-slate-300">|</span>
                  <button onClick={() => setPermData(getDefaultPerms(permUser.role) || {})} className="text-xs text-blue-600 font-medium hover:underline" data-testid="reset-defaults">Reset to Role Defaults</button>
                </div>
              </div>
              <div className="p-5">
                {getSectionsForRole(permUser.role).map(section => {
                  const sectionModules = getModulesForRole(permUser.role).filter(m => m.section === section);
                  if (sectionModules.length === 0) return null;
                  return (
                    <div key={section} className="mb-5 last:mb-0">
                      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{section}</h3>
                      <div className="space-y-1">
                        {sectionModules.map(mod => {
                          const isOn = permData[mod.key] === true;
                          return (
                            <div
                              key={mod.key}
                              className={`flex items-center justify-between p-3 rounded-sm border transition-all ${isOn ? 'bg-green-50/50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                              data-testid={`perm-row-${mod.key}`}
                            >
                              <span className={`text-sm font-medium ${isOn ? 'text-slate-900' : 'text-slate-400'}`}>{mod.label}</span>
                              <button
                                onClick={() => togglePerm(mod.key)}
                                className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none ${isOn ? 'bg-green-500' : 'bg-slate-300'}`}
                                data-testid={`perm-toggle-${mod.key}`}
                              >
                                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${isOn ? 'left-[22px]' : 'left-0.5'}`} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-5 border-t border-slate-200 flex space-x-3">
                <Button variant="outline" onClick={() => setPermUser(null)} className="flex-1 rounded-sm">Cancel</Button>
                <Button onClick={handleSavePermissions} disabled={saving} className="flex-1 bg-[#002249] hover:bg-[#001630] rounded-sm" data-testid="save-permissions-btn">
                  <Shield className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Permissions'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </ModalPortal>

      {/* Admin Access Control Modal — available for ANY user (Admin controls manually) */}
      <ModalPortal>
        {adminAccessUser && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAdminAccessUser(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-sm max-w-xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="admin-access-modal">
              <div className="p-4 sm:p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-lg font-bold text-[#002249]">Manage Access</h2>
                    <p className="text-[12px] text-slate-500 mt-0.5 truncate">{adminAccessUser.name} · {getUserTypeInfo(adminAccessUser.user_type || 'member').label}</p>
                  </div>
                  <button onClick={() => setAdminAccessUser(null)} className="p-1 hover:bg-slate-100 rounded flex-shrink-0"><X className="w-5 h-5 text-slate-400" /></button>
                </div>
              </div>
              <div className="p-5">
                {/* Master Toggle */}
                <div className={`flex items-center justify-between p-4 rounded-lg border-2 mb-5 transition-all ${adminAccessData.admin_access ? 'bg-purple-50 border-purple-300' : 'bg-slate-50 border-slate-200'}`} data-testid="admin-access-toggle-row">
                  <div>
                    <p className="text-sm font-bold text-slate-900">Enable Admin Portal Access</p>
                    <p className="text-xs text-slate-500 mt-0.5">Allow this leadership user to access the admin dashboard</p>
                  </div>
                  <button
                    onClick={() => setAdminAccessData(prev => ({ ...prev, admin_access: !prev.admin_access }))}
                    className={`relative w-12 h-7 rounded-full transition-colors focus:outline-none ${adminAccessData.admin_access ? 'bg-purple-600' : 'bg-slate-300'}`}
                    data-testid="admin-access-master-toggle"
                  >
                    <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform ${adminAccessData.admin_access ? 'left-[22px]' : 'left-0.5'}`} />
                  </button>
                </div>

                {/* Activity Visibility Toggle — lets non-super-admin staff see the User Presence dashboard */}
                <div className={`flex items-center justify-between p-4 rounded-lg border-2 mb-5 transition-all ${adminAccessData.activity_visibility_granted ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`} data-testid="activity-access-row">
                  <div className="min-w-0 pr-3">
                    <p className="text-sm font-bold text-slate-900">Grant Activity Access</p>
                    <p className="text-xs text-slate-500 mt-0.5">Allow this user to view the User Presence dashboard (online members, last seen, last login).</p>
                  </div>
                  <button
                    onClick={() => setAdminAccessData(prev => ({ ...prev, activity_visibility_granted: !prev.activity_visibility_granted }))}
                    className={`relative w-12 h-7 rounded-full transition-colors focus:outline-none flex-shrink-0 ${adminAccessData.activity_visibility_granted ? 'bg-emerald-600' : 'bg-slate-300'}`}
                    data-testid="activity-access-toggle"
                  >
                    <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-sm transition-transform ${adminAccessData.activity_visibility_granted ? 'left-[22px]' : 'left-0.5'}`} />
                  </button>
                </div>

                {/* Module toggles (only visible when admin access is enabled) */}
                {adminAccessData.admin_access && (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-bold text-slate-700">Module Access</p>
                      <div className="flex space-x-2">
                        <button onClick={() => setAllAdminModules(true)} className="text-xs text-green-600 font-medium hover:underline" data-testid="admin-enable-all">Enable All</button>
                        <span className="text-slate-300">|</span>
                        <button onClick={() => setAllAdminModules(false)} className="text-xs text-red-600 font-medium hover:underline" data-testid="admin-disable-all">Disable All</button>
                      </div>
                    </div>
                    {['Membership', 'Operations', 'Content', 'Marketing', 'Settings'].map(section => {
                      const sectionMods = ADMIN_MODULES_LIST.filter(m => m.section === section);
                      if (sectionMods.length === 0) return null;
                      return (
                        <div key={section} className="mb-4 last:mb-0">
                          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{section}</h3>
                          <div className="space-y-1">
                            {sectionMods.map(mod => {
                              const isOn = adminAccessData.admin_modules[mod.key] === true;
                              return (
                                <div
                                  key={mod.key}
                                  className={`flex items-center justify-between p-3 rounded-sm border transition-all ${isOn ? 'bg-green-50/50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                                  data-testid={`admin-mod-row-${mod.key}`}
                                >
                                  <span className={`text-sm font-medium ${isOn ? 'text-slate-900' : 'text-slate-400'}`}>{mod.label}</span>
                                  <button
                                    onClick={() => toggleAdminModule(mod.key)}
                                    className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none ${isOn ? 'bg-green-500' : 'bg-slate-300'}`}
                                    data-testid={`admin-mod-toggle-${mod.key}`}
                                  >
                                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${isOn ? 'left-[22px]' : 'left-0.5'}`} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
              <div className="p-5 border-t border-slate-200 flex space-x-3">
                <Button variant="outline" onClick={() => setAdminAccessUser(null)} className="flex-1 rounded-sm">Cancel</Button>
                <Button onClick={handleSaveAdminAccess} disabled={saving} className="flex-1 bg-purple-600 hover:bg-purple-700 rounded-sm" data-testid="save-admin-access-btn">
                  <Settings className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Admin Access'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </ModalPortal>
    </div>
  );
};

export default UsersManagementPage;
